import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { MapPin, Calendar, Users, ArrowRight, Compass } from "lucide-react";
import { motion } from "framer-motion";
import { STORE } from "@/lib/images";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUPPORTED_CITIES = ["Dallas", "Fort Worth", "Austin", "New Orleans"];

// Free IP→city via ipapi.co (no key, generous limits)
async function detectCity() {
  try {
    const res = await axios.get("https://ipapi.co/json/", { timeout: 4000 });
    const c = (res.data?.city || "").trim();
    // Normalize to our supported cities
    if (/dallas/i.test(c)) return "Dallas";
    if (/fort worth|ft\.?\s*worth|arlington|grapevine|euless|bedford|hurst|north richland/i.test(c)) return "Fort Worth";
    if (/austin|round rock|cedar park|pflugerville|leander/i.test(c)) return "Austin";
    if (/new orleans|nola|metairie|kenner|gretna/i.test(c)) return "New Orleans";
    return c || "Dallas";
  } catch {
    return "Dallas";
  }
}

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

export default function ActivitiesNearYou() {
  const [city, setCity] = useState("Dallas");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const c = await detectCity();
      setCity(c);
      await loadFor(c);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFor = async (c) => {
    setLoading(true);
    try {
      const supported = SUPPORTED_CITIES.includes(c);
      const res = await axios.get(`${API}/activities${supported ? `?city=${encodeURIComponent(c)}` : ""}`);
      setItems(res.data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCityChange = (c) => {
    setCity(c);
    loadFor(c);
  };

  const supported = SUPPORTED_CITIES.includes(city);

  return (
    <section id="activities" className="py-24 md:py-32 px-6 md:px-12 bg-background" data-testid="activities-near-you">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp} className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold mb-4 flex items-center gap-2">
              <Compass size={12} /> Activities near you
            </div>
            <h2 className="font-display font-black tracking-tighter text-4xl md:text-6xl leading-[0.95]">
              {supported ? <>What&apos;s happening in <span className="text-primary">{city}</span>.</> : <>Bring FOTR to <span className="text-primary">{city}</span>.</>}
            </h2>
            <p className="mt-4 text-muted text-lg max-w-xl">
              {supported
                ? "Real meetups members are creating right now. See one you love? Open the app to claim a seat."
                : "We're not live in your city yet — but you can be the first. Join the waitlist below."}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {SUPPORTED_CITIES.map((c) => (
              <button
                key={c}
                onClick={() => handleCityChange(c)}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                  city === c
                    ? "bg-ink text-white border-ink"
                    : "bg-surface text-ink border-line hover:border-primary"
                }`}
                data-testid={`city-toggle-${c.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {c}
              </button>
            ))}
          </div>
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[0,1,2].map((i) => (
              <div key={i} className="aspect-[5/3] rounded-3xl bg-surface-alt animate-pulse border border-line" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <motion.div {...fadeUp} className="bg-surface border border-line rounded-3xl p-10 text-center" data-testid="activities-empty">
            <div className="font-display font-black text-2xl">No live activities {supported ? `in ${city} yet` : "in your area yet"}.</div>
            <p className="text-muted mt-2 max-w-md mx-auto">The community is just spinning up here. Join the beta and you&apos;ll be the first to know — or create the first activity yourself.</p>
            <Link to="/join" className="mt-6 inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-full font-semibold hover:bg-primary-600 transition-colors" data-testid="activities-empty-join">
              Join the beta <ArrowRight size={16} />
            </Link>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.slice(0, 9).map((a, i) => (
              <ActivityCard key={a.id} a={a} index={i} />
            ))}
          </div>
        )}

        {items.length > 0 && (
          <motion.div {...fadeUp} className="mt-10 flex flex-wrap items-center gap-3">
            <a
              href={STORE.ios}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-ink text-white px-6 py-3.5 rounded-full font-semibold inline-flex items-center gap-2"
              data-testid="activities-cta-app"
            >
              Open in the app <ArrowRight size={16} />
            </a>
            <span className="text-sm text-muted">Claim seats, chat with the host, get reminders.</span>
          </motion.div>
        )}
      </div>
    </section>
  );
}

function ActivityCard({ a, index }) {
  const when = a.starts_at
    ? new Date(a.starts_at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "TBA";
  return (
    <motion.div
      {...fadeUp}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08 }}
      className="bg-surface border border-line rounded-3xl p-6 flex flex-col hover:-translate-y-0.5 hover:shadow-xl transition-all"
      data-testid={`activity-card-${a.id}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-primary">{a.city}</span>
        {a.category && <span className="text-[10px] uppercase tracking-[0.2em] text-muted">· {a.category}</span>}
      </div>
      <div className="font-display font-black text-2xl leading-tight">{a.title}</div>
      {a.description && <p className="text-sm text-muted mt-2 line-clamp-3">{a.description}</p>}
      <div className="mt-4 pt-4 border-t border-line space-y-1.5 text-sm">
        {a.location && (
          <div className="flex items-center gap-2 text-muted"><MapPin size={13} className="text-primary" /> {a.location}</div>
        )}
        <div className="flex items-center gap-2 text-muted"><Calendar size={13} className="text-primary" /> {when}</div>
        {a.slots && (
          <div className="flex items-center gap-2 text-muted"><Users size={13} className="text-primary" /> {a.slots} slots</div>
        )}
      </div>
      {a.host && <div className="mt-3 text-xs text-muted">Hosted by <span className="font-semibold text-ink">{a.host}</span></div>}
    </motion.div>
  );
}
